import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Box, Stack, Typography } from '@mui/material';
import { neutral } from '../../theme/colors';
import { baseChartOptions, inr, inrCompact, num, palette, pct } from './report-theme';

const merge = (...parts: ApexOptions[]): ApexOptions =>
	parts.reduce((acc, part) => ({ ...acc, ...part }), {} as ApexOptions);

/** Round up to a value the axis can divide into clean ticks (1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10 × 10ⁿ). */
const niceCeil = (value: number): number => {
	if (value <= 0) return 0;
	const exponent = Math.floor(Math.log10(value));
	const base = 10 ** exponent;
	const mantissa = value / base;
	const nice = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((step) => mantissa <= step) ?? 10;
	return nice * base;
};

/* ------------------------------------------------------------------ *
 * Trend — revenue over time. Area for one series, lines for a compare.
 * ------------------------------------------------------------------ */

interface TrendChartProps {
	categories: string[];
	series: { name: string; data: number[] }[];
	height?: number;
}

export const TrendChart = ({ categories, series, height = 320 }: TrendChartProps) => {
	const multi = series.length > 1;
	const base = baseChartOptions();
	const options: ApexOptions = merge(base, {
		chart: { ...base.chart, type: multi ? 'line' : 'area' },
		colors: multi ? [palette.categorical[0], palette.muted] : [palette.single],
		stroke: { curve: 'smooth', width: 2, lineCap: 'round' },
		fill: multi
			? { type: 'solid', opacity: 0 }
			: {
				type: 'gradient',
				gradient: { shadeIntensity: 0, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 100] },
			},
		markers: {
			size: categories.length <= 14 ? 4 : 0,
			strokeWidth: 2,
			strokeColors: '#fff',
			hover: { size: 6 },
		},
		legend: multi
			? { show: true, position: 'top', horizontalAlign: 'left', offsetY: -8, markers: { radius: 4 } }
			: { show: false },
		xaxis: {
			...base.xaxis,
			categories,
			tickAmount: categories.length > 12 ? 10 : undefined,
			labels: {
				...(base.xaxis as any)?.labels,
				rotate: -35,
				rotateAlways: false,
				hideOverlappingLabels: true,
				trim: false,
			},
		},
		yaxis: {
			...base.yaxis,
			min: 0,
			forceNiceScale: true,
			labels: { ...(base.yaxis as any)?.labels, formatter: (value: number) => inrCompact(value) },
		},
		tooltip: { ...base.tooltip, shared: true, intersect: false, y: { formatter: (value: number) => inr(value) } },
	});

	return <ReactApexChart options={options} series={series} type={multi ? 'line' : 'area'} height={height} />;
};

/* ------------------------------------------------------------------ *
 * Rank — magnitude across named categories. One series, one colour;
 * the top row is emphasised because it is the answer people look for.
 * ------------------------------------------------------------------ */

interface RankBarProps {
	categories: string[];
	values: number[];
	height?: number;
	/** Format for the value label at the end of each bar. */
	formatter?: (value: number) => string;
	/** Format for the axis ticks — compact by default, so ₹12,400 reads as ₹12.4K. */
	axisFormatter?: (value: number) => string;
}

export const RankBar = ({ categories, values, height, formatter = inr, axisFormatter = inrCompact }: RankBarProps) => {
	const base = baseChartOptions();
	const top = values.reduce((max, value) => Math.max(max, value), 0);
	/* Headroom for the end label, rounded so the ticks land on clean numbers. */
	const axisMax = niceCeil(top * 1.18);
	const barHeight = 38;
	const resolvedHeight = height ?? Math.max(180, categories.length * barHeight + 48);

	const options: ApexOptions = merge(base, {
		chart: { ...base.chart, type: 'bar' },
		plotOptions: {
			bar: {
				horizontal: true,
				barHeight: '62%',
				borderRadius: 4,
				borderRadiusApplication: 'end',
				distributed: false,
				dataLabels: { position: 'top' },
			},
		},
		colors: [palette.single],
		dataLabels: {
			enabled: true,
			textAnchor: 'start',
			offsetX: 8,
			formatter: (value: number) => formatter(value),
			style: { fontSize: '12px', fontWeight: 500, colors: [neutral[600]] },
			background: { enabled: false },
		},
		grid: { ...base.grid, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
		xaxis: {
			...base.xaxis,
			categories,
			min: 0,
			max: axisMax || undefined,
			tickAmount: 5,
			labels: { ...(base.xaxis as any)?.labels, formatter: (value: string) => axisFormatter(Number(value)) },
		},
		yaxis: {
			labels: {
				style: { colors: neutral[700], fontSize: '13px', fontFamily: 'inherit' },
				maxWidth: 190,
			},
		},
		tooltip: { ...base.tooltip, y: { formatter: (value: number) => formatter(value) } },
	});

	return (
		<ReactApexChart
			options={options}
			series={[{ name: 'Revenue', data: values }]}
			type="bar"
			height={resolvedHeight}
		/>
	);
};

/* ------------------------------------------------------------------ *
 * Stacked columns — two series that add up to a whole per category.
 * ------------------------------------------------------------------ */

interface StackedColumnsProps {
	categories: string[];
	series: { name: string; data: number[] }[];
	height?: number;
}

export const StackedColumns = ({ categories, series, height = 320 }: StackedColumnsProps) => {
	const base = baseChartOptions();
	const options: ApexOptions = merge(base, {
		chart: { ...base.chart, type: 'bar', stacked: true },
		plotOptions: {
			bar: {
				columnWidth: categories.length <= 2 ? '26%' : categories.length <= 4 ? '40%' : '58%',
				borderRadius: 4,
				borderRadiusApplication: 'end',
				borderRadiusWhenStacked: 'last',
			},
		},
		colors: [palette.categorical[0], palette.categorical[1]],
		/* 2px surface gap between stacked segments instead of a border. */
		stroke: { show: true, width: 2, colors: ['#fff'] },
		legend: { show: true, position: 'top', horizontalAlign: 'left', offsetY: -8, markers: { radius: 4 } },
		xaxis: {
			...base.xaxis,
			categories,
			labels: { ...(base.xaxis as any)?.labels, trim: true, hideOverlappingLabels: true },
		},
		yaxis: {
			...base.yaxis,
			labels: { ...(base.yaxis as any)?.labels, formatter: (value: number) => inrCompact(value) },
		},
		tooltip: { ...base.tooltip, shared: true, intersect: false, y: { formatter: (value: number) => inr(value) } },
	});

	return <ReactApexChart options={options} series={series} type="bar" height={height} />;
};

/* ------------------------------------------------------------------ *
 * Share bar — part-to-whole for a handful of nominal classes. Replaces
 * a donut: the same glance, plus the actual amounts beside each class.
 * ------------------------------------------------------------------ */

interface Slice {
	label: string;
	value: number;
}

export const ShareBar = ({ slices, valueFormatter = inr }: { slices: Slice[]; valueFormatter?: (value: number) => string }) => {
	const total = slices.reduce((sum, slice) => sum + slice.value, 0);
	const visible = slices.filter((slice) => slice.value > 0);

	if (!total || !visible.length) {
		return (
			<Typography variant="body2" color="text.secondary">
				Nothing recorded in this period.
			</Typography>
		);
	}

	return (
		<Box>
			<Stack
				direction="row"
				sx={{ height: 14, borderRadius: 999, overflow: 'hidden', backgroundColor: neutral[100] }}
			>
				{visible.map((slice, index) => (
					<Box
						key={slice.label}
						title={`${slice.label}: ${valueFormatter(slice.value)}`}
						sx={{
							width: `${(slice.value / total) * 100}%`,
							backgroundColor: palette.categorical[index % palette.categorical.length],
							/* 2px surface gap rather than a stroke around each fill. */
							borderRight: index < visible.length - 1 ? '2px solid #fff' : 'none',
						}}
					/>
				))}
			</Stack>

			<Stack spacing={1.25} sx={{ mt: 2.5 }}>
				{slices.map((slice, index) => (
					<Stack key={slice.label} direction="row" alignItems="center" spacing={1.5}>
						<Box
							sx={{
								width: 8,
								height: 8,
								borderRadius: '50%',
								flexShrink: 0,
								backgroundColor: palette.categorical[index % palette.categorical.length],
								opacity: slice.value > 0 ? 1 : 0.3,
							}}
						/>
						<Typography variant="body2" sx={{ flexGrow: 1, color: 'text.primary' }}>
							{slice.label}
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
							{valueFormatter(slice.value)}
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ width: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
						>
							{pct(total ? (slice.value / total) * 100 : 0, 0)}
						</Typography>
					</Stack>
				))}
			</Stack>
		</Box>
	);
};

/* ------------------------------------------------------------------ *
 * Hour columns — when the money comes in. The busiest hour is the
 * point, so it is highlighted and the rest recede.
 * ------------------------------------------------------------------ */

interface HourColumnsProps {
	hours: { hour: number; revenue: number; orders: number }[];
	height?: number;
}

const hourLabel = (hour: number) => {
	const suffix = hour < 12 ? 'am' : 'pm';
	const display = hour % 12 === 0 ? 12 : hour % 12;
	return `${display}${suffix}`;
};

export const HourColumns = ({ hours, height = 240 }: HourColumnsProps) => {
	const active = hours.filter((bucket) => bucket.revenue > 0);
	const first = active.length ? Math.max(0, Math.min(...active.map((b) => b.hour)) - 1) : 8;
	const last = active.length ? Math.min(23, Math.max(...active.map((b) => b.hour)) + 1) : 21;
	const window = hours.slice(first, last + 1);
	const peak = window.reduce((max, bucket) => Math.max(max, bucket.revenue), 0);

	const base = baseChartOptions();
	const options: ApexOptions = merge(base, {
		chart: { ...base.chart, type: 'bar' },
		plotOptions: { bar: { columnWidth: '64%', borderRadius: 4, borderRadiusApplication: 'end', distributed: true } },
		/* Emphasis: the peak hour carries the accent, every other hour recedes. */
		colors: window.map((bucket) =>
			bucket.revenue === peak && peak > 0 ? palette.single : neutral[200]
		),
		legend: { show: false },
		xaxis: {
			...base.xaxis,
			categories: window.map((bucket) => hourLabel(bucket.hour)),
			labels: { ...(base.xaxis as any)?.labels, hideOverlappingLabels: true },
		},
		yaxis: {
			...base.yaxis,
			labels: { ...(base.yaxis as any)?.labels, formatter: (value: number) => inrCompact(value) },
		},
		tooltip: {
			...base.tooltip,
			custom: ({ dataPointIndex }: any) => {
				const bucket = window[dataPointIndex];
				return `<div style="padding:8px 12px;font-family:inherit">
					<div style="font-weight:600;margin-bottom:2px">${hourLabel(bucket.hour)}</div>
					<div>${inr(bucket.revenue)} · ${num(bucket.orders)} order${bucket.orders === 1 ? '' : 's'}</div>
				</div>`;
			},
		},
	});

	return (
		<ReactApexChart
			options={options}
			series={[{ name: 'Revenue', data: window.map((bucket) => bucket.revenue) }]}
			type="bar"
			height={height}
		/>
	);
};
